/**
 * PersonalboqenAbschlussModal.jsx
 *
 * Zweistufiges Modal zum Abschließen des Personalbogens:
 *   1. Admin-Unterschrift (PFLICHT)
 *   2. Mitarbeiter-Unterschrift (OPTIONAL – Vor Ort)
 *
 * Nach dem Abschluss: Status "admin_filled" (nur Admin) oder "approved" (beide)
 * Mitarbeiter-Datum: Datepicker (auch Vergangenheit möglich, für Papier-Erfassung)
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, Loader2, PenLine, X, ChevronDown, ChevronUp,
  Calendar, User, Building2, Info
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import SignaturePad from './ui/SignaturePad';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function today() {
  return new Date().toISOString().slice(0, 10);
}

export function PersonalboqenAbschlussModal({ employee, existingAgSignature, onClose, onDone }) {
  // Admin-Unterschrift
  const agSigRef = useRef(null);
  const [agDatum, setAgDatum] = useState(today());
  const [agOrt, setAgOrt] = useState('');
  const [agSigError, setAgSigError] = useState(false);

  // Mitarbeiter-Unterschrift (optional)
  const [maJetzt, setMaJetzt] = useState(false);
  const maSigRef = useRef(null);
  const [maDatum, setMaDatum] = useState(today());
  const [maOrt, setMaOrt] = useState('');
  const [maSigError, setMaSigError] = useState(false);

  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    // Admin-Sig prüfen (außer wenn bereits vorhanden)
    let agSig = existingAgSignature || null;
    if (!existingAgSignature) {
      agSig = agSigRef.current?.getCanvas().toDataURL('image/png');
      if (!agSig || agSigRef.current?.isEmpty()) {
        setAgSigError(true);
        toast.error('Bitte Admin-Unterschrift leisten');
        return;
      }
    }

    // MA-Sig prüfen wenn gewählt
    let maSig = null;
    if (maJetzt) {
      maSig = maSigRef.current?.getCanvas().toDataURL('image/png');
      if (!maSig || maSigRef.current?.isEmpty()) {
        setMaSigError(true);
        toast.error('Bitte Mitarbeiter-Unterschrift einholen');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        signature_arbeitgeber: agSig,
        datum_unterschrift_ag: agDatum,
        ort_ag: agOrt,
        ...(maSig ? {
          signature_arbeitnehmer: maSig,
          datum_unterschrift_an: maDatum,
          ort_an: maOrt,
        } : {}),
      };
      const res = await axios.post(
        `${API}/api/personalbogen/by-employee/${employee.id}/admin-sign`,
        payload,
        { headers: authHeader() }
      );
      const status = res.data?.status;
      if (status === 'approved') {
        toast.success('Personalbogen vollständig abgeschlossen (beide Unterschriften)!');
      } else {
        toast.success('Personalbogen abgeschlossen – Admin hat unterschrieben');
      }
      onDone(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Abschließen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Personalbogen abschließen</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{employee.name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollbarer Content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">

          {/* ─── Admin-Unterschrift ─── */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800 dark:text-white">Admin-Unterschrift</p>
                <p className="text-xs text-muted-foreground">Pflichtfeld – du als Arbeitgeber unterschreibst</p>
              </div>
              {existingAgSignature && (
                <span className="ml-auto text-xs text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Vorhanden
                </span>
              )}
            </div>

            {existingAgSignature ? (
              <div className="p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-700 rounded-xl">
                <p className="text-xs text-green-700 dark:text-green-400">
                  Deine Unterschrift ist bereits gespeichert. Du musst nicht erneut unterschreiben.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Datum</Label>
                    <Input
                      type="date"
                      value={agDatum}
                      onChange={e => setAgDatum(e.target.value)}
                      max={today()}
                      className="h-9 text-sm"
                      data-testid="ag-datum-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ort</Label>
                    <Input
                      type="text"
                      placeholder="z.B. Stuttgart"
                      value={agOrt}
                      onChange={e => setAgOrt(e.target.value)}
                      className="h-9 text-sm"
                      data-testid="ag-ort-input"
                    />
                  </div>
                </div>
                <div className={`rounded-xl overflow-hidden border-2 ${agSigError ? 'border-red-400' : 'border-gray-200 dark:border-gray-700'}`}>
                  <SignaturePad
                    ref={agSigRef}
                    label="Hier unterschreiben (Admin/Arbeitgeber)"
                    onBegin={() => setAgSigError(false)}
                  />
                </div>
                {agSigError && <p className="text-xs text-red-500">Bitte unterschreiben</p>}
              </>
            )}
          </div>

          {/* ─── Mitarbeiter-Unterschrift (Optional) ─── */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setMaJetzt(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              data-testid="ma-sign-toggle"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-800 dark:text-white">
                    Mitarbeiter unterschreibt jetzt
                    <span className="ml-2 text-xs text-muted-foreground font-normal">(optional)</span>
                  </p>
                  <p className="text-xs text-muted-foreground">Mitarbeiter unterschreibt direkt auf diesem Gerät</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`w-10 h-5 rounded-full transition-colors ${maJetzt ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow mt-0.5 mx-0.5 transition-transform ${maJetzt ? 'translate-x-5' : ''}`} />
                </div>
              </div>
            </button>

            {maJetzt && (
              <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-800">
                {/* Info-Box für Papier-Nacherfassung */}
                <div className="flex items-start gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/10 rounded-lg mt-3">
                  <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 dark:text-blue-400">
                    Mitarbeiter hat bereits auf Papier unterschrieben? Wähle das Datum des Papier-Dokuments.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Datum Mitarbeiter
                    </Label>
                    <Input
                      type="date"
                      value={maDatum}
                      onChange={e => setMaDatum(e.target.value)}
                      className="h-9 text-sm"
                      data-testid="ma-datum-input"
                    />
                    <p className="text-xs text-muted-foreground">Automatisch gesetzt auf heute, änderbar für Papier-Fälle</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ort</Label>
                    <Input
                      type="text"
                      placeholder="z.B. Stuttgart"
                      value={maOrt}
                      onChange={e => setMaOrt(e.target.value)}
                      className="h-9 text-sm"
                      data-testid="ma-ort-input"
                    />
                  </div>
                </div>

                <div className={`rounded-xl overflow-hidden border-2 ${maSigError ? 'border-red-400' : 'border-gray-200 dark:border-gray-700'}`}>
                  <SignaturePad
                    ref={maSigRef}
                    label="Mitarbeiter unterschreibt hier"
                    onBegin={() => setMaSigError(false)}
                  />
                </div>
                {maSigError && <p className="text-xs text-red-500">Bitte Mitarbeiter unterschreiben lassen</p>}
              </div>
            )}
          </div>

          {/* Hinweis: danach kein Link mehr */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 rounded-xl">
            <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Nach dem Abschließen kann der Mitarbeiter <strong>keinen Link mehr erhalten</strong>. 
              Eine nachträgliche Unterschrift ist nur noch Vor Ort möglich.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="flex-1 whitespace-normal leading-tight h-auto min-h-10 py-2" data-testid="admin-sign-submit-btn">
            {saving ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Wird gespeichert…</>
            ) : (
              <><PenLine className="h-4 w-4 mr-2" />
                {maJetzt ? 'Abschließen (beide unterschrieben)' : 'Abschließen (Admin-Unterschrift)'}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}


/**
 * MitarbeiterNachtragsSignModal – für nachträgliche MA-Unterschrift (Vor Ort)
 * Wird gezeigt wenn status = "admin_filled" und MA noch nicht unterschrieben hat
 */
export function MitarbeiterNachtragsSignModal({ employee, onClose, onDone }) {
  const maSigRef = useRef(null);
  const [maDatum, setMaDatum] = useState(today());
  const [maOrt, setMaOrt] = useState('');
  const [sigError, setSigError] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const maSig = maSigRef.current?.getCanvas().toDataURL('image/png');
    if (!maSig || maSigRef.current?.isEmpty()) {
      setSigError(true);
      toast.error('Bitte Mitarbeiter unterschreiben lassen');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/personalbogen/by-employee/${employee.id}/employee-sign-onsite`,
        { signature_arbeitnehmer: maSig, datum_unterschrift_an: maDatum, ort_an: maOrt },
        { headers: authHeader() }
      );
      toast.success('Mitarbeiter-Unterschrift gespeichert – Personalbogen vollständig!');
      onDone(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Mitarbeiter-Unterschrift nachholen</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{employee.name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700 rounded-xl">
            <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 dark:text-blue-400">
              Admin hat bereits unterschrieben. Mitarbeiter kann jetzt nachträglich Vor Ort unterschreiben.
              Wähle das Datum wann der Mitarbeiter unterschrieben hat.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Datum der Unterschrift
              </Label>
              <Input type="date" value={maDatum} onChange={e => setMaDatum(e.target.value)} className="h-9 text-sm" data-testid="nachtrags-datum" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ort</Label>
              <Input type="text" placeholder="z.B. Stuttgart" value={maOrt} onChange={e => setMaOrt(e.target.value)} className="h-9 text-sm" />
            </div>
          </div>

          <div className={`rounded-xl overflow-hidden border-2 ${sigError ? 'border-red-400' : 'border-gray-200 dark:border-gray-700'}`}>
            <SignaturePad ref={maSigRef} label="Mitarbeiter unterschreibt hier" onBegin={() => setSigError(false)} />
          </div>
          {sigError && <p className="text-xs text-red-500">Bitte unterschreiben</p>}

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1" disabled={saving}>Abbrechen</Button>
            <Button onClick={handleSubmit} disabled={saving} className="flex-1" data-testid="nachtrags-sign-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Unterschrift speichern
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
